import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const TIER_CONFIG = {
  Platinum: { color: '#7c3aed', bg: '#f5f3ff', label: 'Platinum', emoji: '💜' },
  Gold:     { color: '#d97706', bg: '#fffbeb', label: 'Gold',     emoji: '🥇' },
  Silver:   { color: '#6b7280', bg: '#f9fafb', label: 'Silver',   emoji: '🥈' },
  Bronze:   { color: '#b45309', bg: '#fef3c7', label: 'Bronze',   emoji: '🥉' },
  Rising:   { color: '#073E81', bg: '#eff6ff', label: 'Rising',   emoji: '📈' },
};

function monthOptions() {
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });
}

function fmtMonth(m) {
  return new Date(m + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function ScoreBar({ value, color = '#073E81', height = 8 }) {
  return (
    <div style={{ flex: 1, height, background: '#f0f0f0', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .5s ease' }} />
    </div>
  );
}

function MetricRow({ label, score, color, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 14, width: 20 }}>{icon}</span>
      <span style={{ fontSize: 12, color: '#555', width: 140, flexShrink: 0 }}>{label}</span>
      <ScoreBar value={score} color={color} />
      <span style={{ fontSize: 12, fontWeight: 700, color, width: 36, textAlign: 'right' }}>{score}</span>
    </div>
  );
}

function TierBadge({ tier }) {
  const c = TIER_CONFIG[tier] || TIER_CONFIG.Rising;
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
      {c.emoji} {c.label}
    </span>
  );
}

function Trend({ value }) {
  if (value === 0) return <span style={{ color: '#999', fontSize: 12 }}>—</span>;
  const up = value > 0;
  return <span style={{ color: up ? '#16a34a' : '#dc2626', fontSize: 12, fontWeight: 600 }}>{up ? '▲' : '▼'} {Math.abs(value)}</span>;
}

function BigScoreRing({ score, tier }) {
  const c = TIER_CONFIG[tier] || TIER_CONFIG.Rising;
  const r = 52, stroke = 8;
  const circ = 2 * Math.PI * r;
  const filled = circ * (score / 100);
  return (
    <svg width={130} height={130} viewBox="0 0 130 130" style={{ flexShrink: 0 }}>
      <circle cx={65} cy={65} r={r} fill="none" stroke="#f0f0f0" strokeWidth={stroke} />
      <circle cx={65} cy={65} r={r} fill="none" stroke={c.color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeLinecap="round"
        transform="rotate(-90 65 65)" />
      <text x={65} y={60} textAnchor="middle" fontSize={28} fontWeight={800} fill={c.color}>{score}</text>
      <text x={65} y={78} textAnchor="middle" fontSize={11} fill="#999">/ 100</text>
    </svg>
  );
}

// ── Owner View ────────────────────────────────────────────────────────────────
function OwnerView({ data, month, onMonthChange }) {
  const [selected, setSelected] = useState(null);

  const person = selected ? data.leaderboard.find(u => u.id === selected) : null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#073E81', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Performance</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>Team Leaderboard · {fmtMonth(month)}</h1>
        </div>
        <select value={month} onChange={e => onMonthChange(e.target.value)}
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #d0d0d0', borderRadius: 8 }}>
          {monthOptions().map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
        </select>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Team Avg Score', value: data.team_avg, color: '#073E81', bg: '#eff6ff' },
          { label: 'Total Entries', value: data.total_entries, color: '#7c3aed', bg: '#f5f3ff' },
          { label: 'Total Present Days', value: data.total_present, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Needs Support', value: data.needs_support.length, color: data.needs_support.length ? '#dc2626' : '#16a34a', bg: data.needs_support.length ? '#fef2f2' : '#f0fdf4' },
        ].map(t => (
          <div key={t.label} style={{ background: t.bg, borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Leaderboard table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#073E81' }}>🏆 Leaderboard</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafa', color: '#999', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Rank</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600 }}>Employee</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, minWidth: 140 }}>Score</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>Trend</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>Entries</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>Corrections</th>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>Attendance</th>
                <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600 }}>Badges</th>
              </tr>
            </thead>
            <tbody>
              {data.leaderboard.map((u, i) => {
                const tc = TIER_CONFIG[u.tier] || TIER_CONFIG.Rising;
                return (
                  <tr key={u.id}
                    onClick={() => setSelected(selected === u.id ? null : u.id)}
                    style={{ borderTop: '1px solid #f5f5f5', cursor: 'pointer', background: selected === u.id ? '#f0f6ff' : 'transparent', transition: 'background .15s' }}
                  >
                    <td style={{ padding: '11px 16px', fontWeight: 800, fontSize: 16, color: i === 0 ? '#d97706' : i === 1 ? '#6b7280' : i === 2 ? '#b45309' : '#bbb' }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${u.rank}`}
                    </td>
                    <td style={{ padding: '11px 8px', fontWeight: 600, color: '#111' }}>{u.name}</td>
                    <td style={{ padding: '11px 8px' }}><TierBadge tier={u.tier} /></td>
                    <td style={{ padding: '11px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ScoreBar value={u.final_score} color={tc.color} />
                        <span style={{ fontWeight: 700, color: tc.color, minWidth: 32 }}>{u.final_score}</span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 8px', textAlign: 'center' }}><Trend value={u.trend} /></td>
                    <td style={{ padding: '11px 8px', textAlign: 'center', color: '#374151' }}>{u.metrics.entries}</td>
                    <td style={{ padding: '11px 8px', textAlign: 'center' }}>
                      <span style={{ color: u.metrics.corrections > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{u.metrics.corrections}</span>
                    </td>
                    <td style={{ padding: '11px 8px', textAlign: 'center', color: '#374151' }}>{u.metrics.present_days}/{u.metrics.working_days}d</td>
                    <td style={{ padding: '11px 8px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.badges.map(b => (
                          <span key={b.key} title={b.label} style={{ fontSize: 16 }}>{b.emoji}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expanded detail panel */}
      {person && (
        <div style={{ background: '#fff', borderRadius: 12, border: `2px solid ${TIER_CONFIG[person.tier]?.color || '#073E81'}`, marginBottom: 20, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <BigScoreRing score={person.final_score} tier={person.tier} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 4 }}>{person.name}</div>
              <TierBadge tier={person.tier} />
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {person.badges.map(b => (
                  <span key={b.key} style={{ background: '#f0f6ff', color: '#073E81', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {b.emoji} {b.label}
                  </span>
                ))}
                {!person.badges.length && <span style={{ fontSize: 12, color: '#999' }}>No badges yet this month</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
            <div>
              <MetricRow label="Work Completed (30%)"   score={person.metrics.work_score}        color="#073E81" icon="📋" />
              <MetricRow label="Quality (20%)"          score={person.metrics.quality_score}     color="#16a34a" icon="💎" />
              <MetricRow label="On-Time Delivery (20%)" score={person.metrics.on_time_score}     color="#7c3aed" icon="⚡" />
            </div>
            <div>
              <MetricRow label="Attendance (10%)"       score={person.metrics.attendance_score}  color="#d97706" icon="📅" />
              <MetricRow label="Team Contribution (10%)"score={person.metrics.team_score}        color="#0891b2" icon="🤝" />
              <MetricRow label="Appreciation (10%)"     score={person.metrics.appreciation_score}color="#db2777" icon="⭐" />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#555', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <span>Entries: <strong>{person.metrics.entries}</strong></span>
            <span>Corrections: <strong style={{ color: person.metrics.corrections > 0 ? '#dc2626' : '#16a34a' }}>{person.metrics.corrections}</strong></span>
            <span>Tasks on time: <strong>{person.metrics.tasks_on_time}/{person.metrics.tasks_with_due}</strong></span>
            <span>Attendance streak: <strong>{person.metrics.streak} days</strong></span>
            <span>Late arrivals: <strong>{person.metrics.late || 0}</strong></span>
          </div>
        </div>
      )}

      {/* Needs Support */}
      {data.needs_support.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#d97706' }}>🤲 Needs Support</span>
          </div>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.needs_support.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fffbeb', borderRadius: 8 }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>{u.issues.join(' · ')}</div>
                </div>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Score: {u.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Employee View ─────────────────────────────────────────────────────────────
function EmployeeView({ data, month, onMonthChange }) {
  const me = data.me;
  const tc = me ? (TIER_CONFIG[me.tier] || TIER_CONFIG.Rising) : TIER_CONFIG.Rising;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#073E81', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>My Performance</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>{fmtMonth(month)}</h1>
        </div>
        <select value={month} onChange={e => onMonthChange(e.target.value)}
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #d0d0d0', borderRadius: 8 }}>
          {monthOptions().map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
        </select>
      </div>

      {me ? (
        <>
          {/* Score card */}
          <div style={{ background: `linear-gradient(135deg, ${tc.color}15, ${tc.color}05)`, border: `2px solid ${tc.color}30`, borderRadius: 16, padding: '24px', marginBottom: 20, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <BigScoreRing score={me.final_score} tier={me.tier} />
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <TierBadge tier={me.tier} />
                <span style={{ fontSize: 13, color: '#555' }}>Rank <strong style={{ color: '#111' }}>#{me.rank}</strong> of {data.leaderboard.length}</span>
                <Trend value={me.trend} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', fontSize: 12, color: '#555', marginBottom: 14 }}>
                <span>Entries: <strong style={{ color: '#111' }}>{me.metrics.entries}</strong></span>
                <span>Corrections: <strong style={{ color: me.metrics.corrections > 0 ? '#dc2626' : '#16a34a' }}>{me.metrics.corrections}</strong></span>
                <span>Present: <strong style={{ color: '#111' }}>{me.metrics.present_days}/{me.metrics.working_days}d</strong></span>
                <span>On-time: <strong style={{ color: '#111' }}>{me.metrics.tasks_on_time}/{me.metrics.tasks_with_due}</strong></span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {me.badges.map(b => (
                  <span key={b.key} style={{ background: tc.bg, color: tc.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    {b.emoji} {b.label}
                  </span>
                ))}
                {!me.badges.length && <span style={{ fontSize: 12, color: '#999' }}>Log more work this month to earn badges!</span>}
              </div>
            </div>
          </div>

          {/* Metric breakdown */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#073E81', marginBottom: 14 }}>📊 Score Breakdown</div>
            <MetricRow label="Work Completed (30%)"   score={me.metrics.work_score}        color="#073E81" icon="📋" />
            <MetricRow label="Quality (20%)"          score={me.metrics.quality_score}     color="#16a34a" icon="💎" />
            <MetricRow label="On-Time Delivery (20%)" score={me.metrics.on_time_score}     color="#7c3aed" icon="⚡" />
            <MetricRow label="Attendance (10%)"       score={me.metrics.attendance_score}  color="#d97706" icon="📅" />
            <MetricRow label="Team Contribution (10%)"score={me.metrics.team_score}        color="#0891b2" icon="🤝" />
            <MetricRow label="Appreciation (10%)"     score={me.metrics.appreciation_score}color="#db2777" icon="⭐" />
          </div>

          {/* AI Coach */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#073E81', marginBottom: 14 }}>🎯 Coach Tips</div>
            {data.tips.map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: '#f8f9ff', borderRadius: 8, marginBottom: 8, fontSize: 13, color: '#374151' }}>
                <span>💡</span><span>{tip}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 60, color: '#999', fontSize: 14 }}>No performance data yet for this month.</div>
      )}

      {/* Leaderboard (top 10, names only) */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#073E81' }}>🏆 Team Leaderboard</span>
        </div>
        <div style={{ padding: '12px 20px' }}>
          {data.leaderboard.map((u, i) => {
            const utc = TIER_CONFIG[u.tier] || TIER_CONFIG.Rising;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < data.leaderboard.length - 1 ? '1px solid #f5f5f5' : 'none', background: u.is_me ? '#f0f6ff' : 'transparent', borderRadius: u.is_me ? 8 : 0, paddingLeft: u.is_me ? 8 : 0 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: i === 0 ? '#d97706' : i === 1 ? '#6b7280' : i === 2 ? '#b45309' : '#ccc', width: 28, textAlign: 'center' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${u.rank}`}
                </span>
                <span style={{ flex: 1, fontWeight: u.is_me ? 700 : 500, color: u.is_me ? '#073E81' : '#374151', fontSize: 13 }}>
                  {u.name} {u.is_me && <span style={{ fontSize: 10, color: '#073E81' }}>← You</span>}
                </span>
                <TierBadge tier={u.tier} />
                <span style={{ fontWeight: 700, color: utc.color, fontSize: 13, minWidth: 30, textAlign: 'right' }}>{u.score}</span>
                <div style={{ display: 'flex', gap: 2 }}>{u.badges.map(b => <span key={b.key} style={{ fontSize: 13 }}>{b.emoji}</span>)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PerformancePage() {
  const { user, isOwner } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const { data, isLoading, error } = useQuery({
    queryKey: ['performance', month],
    queryFn: () => api.get(`/performance?month=${month}`).then(r => r.data),
  });

  if (isLoading) return <div style={{ textAlign: 'center', padding: 80, color: '#999' }}>Calculating scores…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: 80, color: '#dc2626' }}>Failed to load performance data.</div>;
  if (!data) return null;

  return (isOwner || isAdmin)
    ? <OwnerView data={data} month={month} onMonthChange={setMonth} />
    : <EmployeeView data={data} month={month} onMonthChange={setMonth} />;
}
